import { useId } from "react";
import type { ReactNode } from "react";
import { classNames } from "./internal/class-names";
import { InfoPopover } from "./info-popover";

export interface ScreenHeadHelp {
  /** The on-demand explanation (Level C in the density model). */
  readonly content: ReactNode;
  /** Localized accessible name for the help trigger, e.g. "Hilfe". */
  readonly label: string;
}

export interface ScreenHeadProps {
  /**
   * Decorative glyph shown in the leading tile. Callers pass their own icon
   * component so this module stays independent of any one icon set.
   */
  readonly icon?: ReactNode | undefined;
  /**
   * Short uppercase kicker above the title. In operational screens this is the
   * workflow step the screen belongs to, so an operator can tell where they are
   * in the sequence without reading the whole heading.
   */
  readonly eyebrow?: ReactNode | undefined;
  readonly title: ReactNode;
  /**
   * One-line statement of what the screen is for, rendered as a permanent
   * paragraph. Prefer `help`: instructional copy is Level C and belongs
   * behind the click-open affordance, not in permanent chrome (ADR-UI-006).
   */
  readonly description?: ReactNode | undefined;
  /** On-demand help rendered as a trailing InfoPopover. */
  readonly help?: ScreenHeadHelp | undefined;
  /** Controls rendered opposite the title — never hidden, they wrap instead. */
  readonly actions?: ReactNode | undefined;
  /** Heading level, so callers keep a correct document outline. */
  readonly headingLevel?: 1 | 2 | 3 | 4 | undefined;
  /** Set when the caller wants to reference the heading with aria-labelledby. */
  readonly titleId?: string | undefined;
  readonly className?: string | undefined;
}

/**
 * Titled band at the top of a workbench screen.
 *
 * Every operational screen opens with the same four facts in the same places:
 * which step of the workflow this is, what the screen is called, what it is
 * for, and what can be done from here. Giving that a single component means an
 * operator moving between screens re-reads a layout they already know instead
 * of re-parsing a fresh heading arrangement each time.
 *
 * The band is a self-contained colour surface: it carries its own foreground
 * (`--thw-color-on-screen-head`) because it inverts against the page in light
 * themes and matches it in dark ones.
 */
export function ScreenHead({
  actions,
  className,
  description,
  eyebrow,
  headingLevel = 2,
  help,
  icon,
  title,
  titleId,
}: ScreenHeadProps): ReactNode {
  const generatedId = useId();
  const headingId = titleId ?? `${generatedId}-title`;
  const Heading = `h${String(headingLevel)}` as "h1" | "h2" | "h3" | "h4";

  return (
    <header className={classNames("thw-screen-head", className)}>
      <div className="thw-screen-head__identity">
        {icon === undefined ? null : (
          <span aria-hidden="true" className="thw-screen-head__icon">
            {icon}
          </span>
        )}
        <div className="thw-screen-head__text">
          {eyebrow === undefined ? null : (
            <p className="thw-screen-head__eyebrow">{eyebrow}</p>
          )}
          <div className="thw-screen-head__title-row">
            <Heading className="thw-screen-head__title" id={headingId}>
              {title}
            </Heading>
          </div>
          {description === undefined ? null : (
            <p className="thw-screen-head__description">{description}</p>
          )}
        </div>
      </div>
      {actions === undefined && help === undefined ? null : (
        <div className="thw-screen-head__trailing">
          {actions === undefined ? null : (
            <div className="thw-screen-head__actions">{actions}</div>
          )}
          {help === undefined ? null : (
            <InfoPopover label={help.label}>{help.content}</InfoPopover>
          )}
        </div>
      )}
    </header>
  );
}
